import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { format } from "date-fns";
import { is, th } from "date-fns/locale";
import SafeIcon from "../common/SafeIcon";
import EmailSharingModal from "./EmailSharingModal";

const {
  FiFileText,
  FiTrash2,
  FiCopy,
  FiCheck,
  FiChevronRight,
  FiChevronLeft,
  FiMail,
  FiBookmark,
  FiEdit3,
  FiSend,
  FiShare,
  FiEdit,
  FiShare2,
} = FiIcons;

const SummaryList = ({
  summaries = [],
  onDeleteSummary,
  meetingDetails,
  onSendEmail,
  onUpdateSummaryTitle,
  onUpdateSummaryContent,
}) => {
  const [copiedId, setCopiedId] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const tabsContainerRef = useRef(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [editingSummaryId, setEditingSummaryId] = useState(null);
  const [editText, setEditText] = useState("");
  const inputRef = useRef(null);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editContentText, setEditContentText] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    setActiveTabIndex(0);

    const checkScroll = () => {
      const container = tabsContainerRef.current;
      if (container) {
        setShowScrollButtons(container.scrollWidth > container.clientWidth);
      }
    };
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [summaries]);
  useEffect(() => {
    if (isEditingContent && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditingContent]);
  useEffect(() => {
    if (editingSummaryId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingSummaryId]);

  const handleCopy = async (content, id) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const handleDelete = (idToDelete) => {
    const summaryToDelete = summaries.find((s) => s.id === idToDelete);
    const userConfirmed = window.confirm(
      `คุณแน่ใจหรือไม่ว่าต้องการลบสรุป:\n\n"${
        summaryToDelete?.title || "รายการที่บันทึกไว้"
      }"`
    );

    if (userConfirmed) {
      onDeleteSummary(idToDelete);
    }
  };

  const scrollTabs = (direction) => {
    if (tabsContainerRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      tabsContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleStartEdit = (e, summary, index) => {
    e.stopPropagation();
    setEditingSummaryId(summary.id);
    setEditText(summary.title || `สรุป #${index + 1}`);
  };

  const handleSaveTitle = () => {
    if (editingSummaryId && editText.trim()) {
      onUpdateSummaryTitle(editingSummaryId, editText.trim());
    }
    setEditingSummaryId(null);
  };

  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveTitle();
    } else if (e.key === "Escape") {
      setEditingSummaryId(null);
    }
  };

  const handleStartContentEdit = () => {
    if (summaries[activeTabIndex]) {
      setEditContentText(summaries[activeTabIndex].content);
      setIsEditingContent(true);
    }
  };

  const handleSaveContent = () => {
    if (summaries[activeTabIndex]) {
      onUpdateSummaryContent(summaries[activeTabIndex].id, editContentText);
      setIsEditingContent(false);
    }
  };

  const handleCancelContentEdit = () => {
    setIsEditingContent(false);
  };

  if (!summaries || summaries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        {/* 1. รูปภาพ Mascot หลัก */}
        <img
          src="/images/yy_bookmark.PNG"
          alt="ยั่งยืน AI"
          className="w-24 h-24 rounded-full mb-4 opacity-80"
        />

        {/* 2. หัวข้อ */}
        <h3 className="text-lg font-semibold text-slate-700 mb-2">
          ยังไม่มีสรุปที่บันทึกไว้
        </h3>

        {/* 3. ข้อความคำแนะนำ */}
        <p className="text-sm text-slate-500 flex items-center gap-1.5">
          <span>กดไอคอน</span>
          <span className="p-1 bg-slate-200 rounded-md">
            <FiBookmark className="w-6 h-6 text-slate-500" />
          </span>
          <span>ที่ข้อความของ "ยั่งยืน" เพื่อบันทึก</span>
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* --- START: Modern Tabs ที่มีปุ่มเลื่อนและซ่อน Scrollbar --- */}
      {/* --- START: Modern Tabs ที่มีปุ่มเลื่อนและซ่อน Scrollbar อย่างถูกต้อง --- */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* 1. ปุ่มเลื่อนซ้าย */}
        <AnimatePresence>
          {showScrollButtons && (
            <motion.button
              onClick={() => scrollTabs("left")}
              className="flex-shrink-0 bg-white hover:bg-slate-100 text-slate-500 p-1 rounded-full border border-slate-200 shadow-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
            </motion.button>
          )}
        </AnimatePresence>

        {/* --- 2. Scrollable Container (แก้ไขที่นี่) --- */}
        {/* กลับมาใช้ div ชั้นเดียวที่เรียบง่าย และเพิ่ม 'scrollbar-hide' */}
        <div
          ref={tabsContainerRef}
          className="flex-1 overflow-x-auto py-1 scrollbar-hide"
        >
          <div className="flex items-center gap-2">
            {summaries.map((summary, index) => (
              <div
                key={summary.id}
                onClick={() =>
                  activeTabIndex !== index &&
                  !editingSummaryId &&
                  setActiveTabIndex(index)
                }
                className={`group flex-shrink-0 px-3 py-1.5 rounded-full whitespace-nowrap transition-all text-sm font-semibold flex items-center gap-1.5 ${
                  activeTabIndex === index
                    ? "bg-green-600 text-white shadow"
                    : "bg-white text-slate-600 border border-slate-300 hover:bg-slate-100 hover:border-slate-400"
                } ${!editingSummaryId ? "cursor-pointer" : ""}`}
              >
                {editingSummaryId === summary.id ? (
                  <input
                    ref={inputRef}
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onBlur={handleSaveTitle}
                    onKeyDown={handleEditKeyDown}
                    className="bg-transparent outline-none focus:ring-0 p-0 border-0 w-36"
                    style={{
                      color: activeTabIndex === index ? "white" : "inherit",
                    }}
                  />
                ) : (
                  <>
                    <span className="truncate max-w-[150px]">
                      {summary.title || `สรุป #${index + 1}`}
                    </span>
                    <button
                      onClick={(e) => handleStartEdit(e, summary, index)}
                      className={`ml-1 transition-opacity opacity-0 group-hover:opacity-70 hover:!opacity-100 ${
                        activeTabIndex === index
                          ? "text-white/80 hover:text-white"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                      title="แก้ไขชื่อ"
                    >
                      <FiEdit3 size={14} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 3. ปุ่มเลื่อนขวา */}
        <AnimatePresence>
          {showScrollButtons && (
            <motion.button
              onClick={() => scrollTabs("right")}
              className="flex-shrink-0 bg-white hover:bg-slate-100 text-slate-500 p-1 rounded-full border border-slate-200 shadow-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
      {/* --- END: Modern Tabs --- */}
      {/* --- END: Modern Tabs --- */}

      <AnimatePresence mode="wait">
        {summaries[activeTabIndex] && (
          <motion.div
            key={summaries[activeTabIndex].id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex-1 overflow-y-auto mt-3 flex flex-col"
          >
            <div className="flex items-center justify-between mb-2 flex-shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <SafeIcon
                  icon={FiFileText}
                  className="w-5 h-5 text-green-600"
                />
                <h4 className="font-semibold text-slate-800 truncate">
                  {summaries[activeTabIndex].title ||
                    `สรุป #${activeTabIndex + 1}`}
                </h4>
              </div>
              {isEditingContent ? (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={handleCancelContentEdit}
                    className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-200 hover:bg-slate-300 rounded-md"
                  >
                    ยกเลิก
                  </button>
                  <button
                    onClick={handleSaveContent}
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-md flex items-center gap-1"
                  >
                    <FiCheck size={14} /> บันทึก
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={handleStartContentEdit}
                    className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-slate-100 rounded-full"
                    title="แก้ไขเนื้อหา"
                  >
                    <SafeIcon icon={FiEdit} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setIsEmailModalOpen(true)}
                    className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-slate-100 rounded-full"
                    title="แชร์สรุป"
                  >
                    <SafeIcon icon={FiShare2} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() =>
                      handleCopy(
                        summaries[activeTabIndex].content,
                        summaries[activeTabIndex].id
                      )
                    }
                    className={`p-1.5 rounded-full ${
                      copiedId === summaries[activeTabIndex].id
                        ? "text-green-600 bg-green-100"
                        : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                    }`}
                    title={
                      copiedId === summaries[activeTabIndex].id
                        ? "คัดลอกแล้ว"
                        : "คัดลอก"
                    }
                  >
                    <SafeIcon
                      icon={
                        copiedId === summaries[activeTabIndex].id
                          ? FiCheck
                          : FiCopy
                      }
                      className="w-4 h-4"
                    />
                  </button>
                  <button
                    onClick={() => handleDelete(summaries[activeTabIndex].id)}
                    className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-slate-100 rounded-full"
                    title="ลบ"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {isEditingContent ? (
              <textarea
                ref={textareaRef}
                value={editContentText}
                onChange={(e) => setEditContentText(e.target.value)}
                className="prose prose-sm max-w-none text-slate-700 bg-white border border-green-400 p-4 rounded-lg flex-1 w-full h-full resize-none focus:outline-none focus:ring-2 focus:ring-green-400"
              />
            ) : (
              <div className="prose prose-sm prose-yungyoen max-w-none bg-slate-100 p-4 rounded-lg flex-1 overflow-y-auto">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {summaries[activeTabIndex].content}
                </ReactMarkdown>
              </div>
            )}

            {isEmailModalOpen && (
              <EmailSharingModal
                isOpen={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                content={summaries[activeTabIndex].content}
                meetingDetails={meetingDetails}
                onSend={onSendEmail}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SummaryList;
